import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  inject,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { CouponService } from '../../../services/coupon.service';
import { TokenStore } from '../../../services/token-store';
import { EligibleCouponRedemption } from '../../../models/coupon';
import { Subscription } from 'rxjs';
import { timeout } from 'rxjs/operators';
import { ApiUrl } from '../../../shared/models/apiurl.models';
import {
  ApiAdRoute,
  ApiPlan,
  BillingPeriod,
  PlanTier,
  adRouteBenefits,
  adRoutePrice,
  allowanceOf,
  benefitsOf,
  buildPlanTiers,
  formatIls,
  lifeSpanLabel,
  monthlyEquivalent,
  planFor,
  priceOf,
  sortAdRoutes,
  yearlySaving,
} from './plan-tiers';

/** Which kind of payment the cards show. */
export type PlanKind = 'ad' | 'subscription';

/**
 * Emitted when the user picks a card.
 * - kind 'ad': `id` = AdsRoute id (per-ad plan, chosen at the end of the publish form).
 * - kind 'subscription': `id` = SubscriptionPlan row of the CURRENT billing period.
 * - kind 'coupon' (SCRUM-313): `id` = the coupon redemption the user explicitly
 *   picked; the publish itself happens in the plan picker at the end of the form.
 */
export interface PickedPlan {
  kind: PlanKind | 'coupon';
  id: number;
  name: string;
  billingPeriod?: BillingPeriod;
}

/** One card's view model — same 5 rows for both kinds so the layout is shared. */
interface CardVm {
  key: string;
  id: number;
  name: string;
  price: string;              // ₪49 / ₪468 / חינם
  free: boolean;
  periodLabel: string;        // לחודש / לשנה / למודעה
  equivalent: string | null;  // subscription yearly only
  saving: string | null;      // subscription yearly only
  chip: string;               // allowance (subscription) / days active (per ad)
  benefits: string[];
  cta: string;
  disabled: boolean;
  note: string | null;        // e.g. free quota used up
}

type LoadState = 'loading' | 'error' | 'ready';

/**
 * Adaline answer to "כמה עולה פרסום?", after the bot asked which payment type:
 *  - kind 'ad'           — AdsRoute per-ad plans (GET client-api/ads-routes/)
 *  - kind 'subscription' — Go / Plus / Pro with a monthly / yearly switch
 *                          (GET client-api/subscriptions/plans/, monthly by default)
 * Only the list of the given kind is loaded. Nothing about plans is hardcoded; see plan-tiers.ts.
 */
@Component({
  selector: 'app-adaline-plan-cards',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './adaline-plan-cards.component.html',
  styleUrls: ['./adaline-plan-cards.component.scss'],
})
export class AdalinePlanCardsComponent implements OnInit, OnDestroy {
  /** Set once by the bot message (ChatMsg.widget); not switchable inside the cards. */
  @Input() kind: PlanKind = 'ad';
  @Output() planSelected = new EventEmitter<PickedPlan>();

  period: BillingPeriod = 'MONTHLY';

  adState: LoadState = 'loading';
  subState: LoadState = 'loading';
  cards: CardVm[] = [];

  /** SCRUM-313: usable coupons (per-ad tab only), server order. Empty = no coupon card. */
  coupons: EligibleCouponRedemption[] = [];
  selectedCouponId: number | null = null;

  private readonly couponService = inject(CouponService);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private couponSub?: Subscription;

  private adRoutes: ApiAdRoute[] = [];
  private tiers: PlanTier[] = [];
  private adSub?: Subscription;
  private planSub?: Subscription;

  constructor(
    private http: HttpClient,
    private apiUrl: ApiUrl,
    private cdr: ChangeDetectorRef,
  ) {}

  get state(): LoadState { return this.kind === 'ad' ? this.adState : this.subState; }

  ngOnInit(): void {
    if (this.kind === 'ad') { this.loadAdRoutes(); this.loadCoupons(); } else this.loadPlans();
  }

  ngOnDestroy(): void {
    this.couponSub?.unsubscribe();
    this.adSub?.unsubscribe();
    this.planSub?.unsubscribe();
  }

  retry(): void {
    if (this.kind === 'ad') this.loadAdRoutes(); else this.loadPlans();
  }

  loadAdRoutes(): void {
    this.adSub?.unsubscribe();
    this.adState = 'loading';
    this.rebuild();
    this.adSub = this.http.get<ApiAdRoute[] | { results?: ApiAdRoute[] }>(this.apiUrl.getClientAdsRouteUrl())
      .pipe(timeout(10000))
      .subscribe({
        next: body => {
          this.adRoutes = sortAdRoutes(Array.isArray(body) ? body : (body?.results ?? []));
          this.adState = 'ready';
          this.rebuild();
        },
        error: () => { this.adState = 'error'; this.rebuild(); },
      });
  }

  loadPlans(): void {
    this.planSub?.unsubscribe();
    this.subState = 'loading';
    this.rebuild();
    this.planSub = this.http.get<ApiPlan[] | { results?: ApiPlan[] }>(this.apiUrl.getSubscriptionPlans())
      .pipe(timeout(10000))
      .subscribe({
        next: body => {
          this.tiers = buildPlanTiers(Array.isArray(body) ? body : (body?.results ?? []));
          this.subState = 'ready';
          this.rebuild();
        },
        error: () => { this.subState = 'error'; this.rebuild(); },
      });
  }

  /** Logged-in users only; a failure = no coupon card (the regular cards still show). */
  loadCoupons(): void {
    this.couponSub?.unsubscribe();
    if (!this.isBrowser || !TokenStore.isLoggedIn()) { this.coupons = []; return; }
    this.couponSub = this.couponService.loadEligible().pipe(timeout(10000)).subscribe({
      next: list => {
        this.coupons = list || [];
        if (!this.coupons.some(c => c.redemption_id === this.selectedCouponId)) this.selectedCouponId = null;
        this.cdr.markForCheck();
      },
      error: () => { this.coupons = []; this.selectedCouponId = null; this.cdr.markForCheck(); },
    });
  }

  selectCoupon(c: EligibleCouponRedemption): void {
    this.selectedCouponId = c.redemption_id;
    this.cdr.markForCheck();
  }

  get selectedCoupon(): EligibleCouponRedemption | null {
    return this.coupons.find(c => c.redemption_id === this.selectedCouponId) ?? null;
  }

  /** Never auto-picked: the button stays disabled until a coupon is chosen. */
  pickCoupon(): void {
    const c = this.selectedCoupon;
    if (!c) return;
    this.planSelected.emit({ kind: 'coupon', id: c.redemption_id, name: c.title });
  }

  setPeriod(p: BillingPeriod): void {
    if (p === this.period) return;
    this.period = p;
    this.rebuild();
  }

  pick(card: CardVm): void {
    if (card.disabled) return;
    this.planSelected.emit({
      kind: this.kind,
      id: card.id,
      name: card.name,
      billingPeriod: this.kind === 'subscription' ? this.period : undefined,
    });
  }

  trackByKey(_: number, c: CardVm): string { return c.key; }

  get emptyText(): string {
    if (this.kind === 'ad') return 'אין כרגע מסלולי פרסום למודעה זמינים.';
    return this.period === 'YEARLY' ? 'אין כרגע מסלולים שנתיים זמינים.' : 'אין כרגע מסלולים חודשיים זמינים.';
  }

  private rebuild(): void {
    this.cards = this.kind === 'ad' ? this.adCards() : this.subscriptionCards();
    this.cdr.markForCheck();
  }

  private adCards(): CardVm[] {
    return this.adRoutes.map(r => {
      const price = adRoutePrice(r);
      const blocked = !!r.blocked;
      return {
        key: 'ad-' + r.id,
        id: r.id,
        name: r.title.trim(),
        price: price > 0 ? formatIls(price) : 'חינם',
        free: price <= 0,
        periodLabel: price > 0 ? 'למודעה' : '',
        equivalent: null,
        saving: null,
        chip: lifeSpanLabel(r),
        benefits: adRouteBenefits(r),
        cta: 'בחר חבילה',
        disabled: blocked,
        note: blocked ? 'ניצלת את מכסת המודעות החינמיות. ניתן לבחור מסלול בתשלום.' : null,
      };
    });
  }

  /** Only tiers that HAVE a row for the selected period are shown - one card per tier. */
  private subscriptionCards(): CardVm[] {
    const yearly = this.period === 'YEARLY';
    return this.tiers
      .map(t => ({ tier: t, plan: planFor(t, this.period) }))
      .filter((x): x is { tier: PlanTier; plan: ApiPlan } => !!x.plan && priceOf(x.plan) != null)
      .map(({ tier, plan }) => {
        const saving = yearly ? yearlySaving(tier) : null;
        return {
          key: 'sub-' + tier.key,
          id: plan.id,
          name: tier.name,
          price: formatIls(priceOf(plan)),
          free: false,
          periodLabel: yearly ? 'לשנה' : 'לחודש',
          equivalent: yearly ? `שווה ערך ל־${formatIls(monthlyEquivalent(tier))} לחודש · בחיוב שנתי` : null,
          saving: saving != null ? `חיסכון של ${formatIls(saving)} בשנה` : null,
          chip: allowanceOf(plan),
          benefits: benefitsOf(plan),
          cta: yearly ? 'בחר מסלול שנתי' : 'בחר מסלול חודשי',
          disabled: false,
          note: null,
        };
      });
  }
}
